import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import { HiOutlinePlus } from "react-icons/hi";
import { AiOutlineEdit, AiOutlineDelete } from "react-icons/ai";
import { MdOutlineSecurity } from "react-icons/md";
import {
  useAddAdminRoleMutation,
  useDeleteAdminRoleMutation,
  useGetAllAdminRolesMutation,
  useUpdateAdminRoleByIdMutation,
} from "../../../features/admin/adminRoles/adminRolesApi";

const AdminRoles = () => {
  const navigate = useNavigate();
  const [roles, setRoles] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [action, setAction] = useState("add");
  const [roleName, setRoleName] = useState("");
  const [selectedId, setSelectedId] = useState(null);

  const [getAllAdminRoles, { data: rolesData, isLoading }] = useGetAllAdminRolesMutation();
  const [addAdminRole, { data: addData, error: addError }] = useAddAdminRoleMutation();
  const [updateAdminRoleById, { data: updateData, error: updateError }] =
    useUpdateAdminRoleByIdMutation();
  const [deleteAdminRole, { data: deleteData, error: deleteError }] =
    useDeleteAdminRoleMutation();

  const token = sessionStorage.getItem("access_token");

  const fetchRoles = () => {
    getAllAdminRoles({ token });
  };

  useEffect(() => {
    fetchRoles();
  }, []);

  useEffect(() => {
    if (rolesData?.body) setRoles(rolesData.body);
  }, [rolesData]);

  useEffect(() => {
    if (addData) {
      toast.success("Admin role created successfully");
      setShowModal(false);
      fetchRoles();
    }
    if (addError) toast.error("Failed to create admin role");
  }, [addData, addError]);

  useEffect(() => {
    if (updateData) {
      toast.success("Admin role updated successfully");
      setShowModal(false);
      fetchRoles();
    }
    if (updateError) toast.error("Failed to update admin role");
  }, [updateData, updateError]);

  useEffect(() => {
    if (deleteData) {
      toast.success("Admin role deleted successfully");
      fetchRoles();
    }
    if (deleteError) toast.error("Failed to delete admin role");
  }, [deleteData, deleteError]);

  const openModal = (type, data = null) => {
    setAction(type);
    if (type === "add") {
      setRoleName("");
      setSelectedId(null);
    } else {
      setRoleName(data.name || "");
      setSelectedId(data.id);
    }
    setShowModal(true);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!roleName.trim()) {
      toast.error("Please enter a role name");
      return;
    }

    if (action === "add") {
      addAdminRole({ token, data: { name: roleName } });
    } else {
      updateAdminRoleById({ token, id: selectedId, data: { name: roleName } });
    }
  };

  return (
    <div className="page-content container-fluid">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4 className="mb-0">Admin Roles & Permissions</h4>
        <button className="btn btn-primary btn-sm" onClick={() => openModal("add")}>
          <HiOutlinePlus className="mr-1" /> Add Role
        </button>
      </div>

      <div className="card">
        <div className="card-body p-0">
          <table className="table mb-0">
            <thead>
              <tr>
                <th>#</th>
                <th>Role Name</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan="3" className="text-center py-4">
                    Loading roles...
                  </td>
                </tr>
              ) : roles.length === 0 ? (
                <tr>
                  <td colSpan="3" className="text-center py-4">
                    No admin roles found.
                  </td>
                </tr>
              ) : (
                roles.map((role, index) => (
                  <tr key={role.id || index}>
                    <td>{index + 1}</td>
                    <td>
                      <strong>{role.name}</strong>
                    </td>
                    <td>
                      <button
                        className="btn btn-sm btn-outline-info mr-2"
                        title="Edit Permissions"
                        onClick={() => navigate("/adminRoles/editPermission", { state: role })}
                      >
                        <MdOutlineSecurity className="mr-1" /> Edit Permissions
                      </button>
                      {role.name !== "Super Admin" && (
                        <>
                          <button
                            className="btn btn-sm btn-outline-secondary mr-2"
                            title="Edit Role Name"
                            onClick={() => openModal("edit", role)}
                          >
                            <AiOutlineEdit />
                          </button>
                          <button
                            className="btn btn-sm btn-outline-danger"
                            title="Delete Role"
                            onClick={() => deleteAdminRole({ token, id: role.id })}
                          >
                            <AiOutlineDelete />
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && (
        <div className="modal fade show d-block" style={{ background: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <form onSubmit={handleSubmit}>
                <div className="modal-header">
                  <h5 className="modal-title">
                    {action === "add" ? "Create Admin Role" : "Edit Admin Role"}
                  </h5>
                  <button type="button" className="close" onClick={() => setShowModal(false)}>
                    <span>&times;</span>
                  </button>
                </div>
                <div className="modal-body">
                  <div className="form-group">
                    <label>Role Name</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="e.g. Finance Manager, Support Admin"
                      value={roleName}
                      onChange={(e) => setRoleName(e.target.value)}
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary">
                    Save Role
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminRoles;
